import type { ContextItem } from '@Types/knowledge';
import { CONTEXT_RAG_THRESHOLD_KB } from '@/services/knowledgeApi';

export type IngestionStatus = 'ready' | 'processing' | 'failed';

const RAG_THRESHOLD_BYTES = CONTEXT_RAG_THRESHOLD_KB * 1024;

/** Uploads at or above the server RAG threshold (passage-indexed documents). */
export const isLargeKnowledgeDocument = (
  item: Pick<ContextItem, 'item_type' | 'file_size_bytes' | 'chunk_count'>,
): boolean => {
  if (item.item_type !== 'document') return false;
  if ((item.chunk_count ?? 0) > 0) return true;
  return (item.file_size_bytes ?? 0) >= RAG_THRESHOLD_BYTES;
};

/** Top-of-card / detail blurb — omit for large docs until summary UX is defined. */
export const knowledgeLibraryBlurb = (item: ContextItem): string | null => {
  const text = (item.summary || item.description || '').trim();
  if (!text) return null;
  if (isLargeKnowledgeDocument(item)) {
    if (text === 'Indexing document for search…') return text;
    return null;
  }
  return text;
};

export const getIngestionStatus = (
  item: Pick<ContextItem, 'item_type' | 'ingestion_status'>,
): IngestionStatus => {
  if (item.item_type !== 'document') return 'ready';
  const status = item.ingestion_status ?? 'ready';
  if (status === 'processing' || status === 'failed' || status === 'ready') {
    return status;
  }
  return 'ready';
};

export const isContextSelectable = (item: ContextItem): boolean =>
  getIngestionStatus(item) === 'ready';

export const sourceFileKind = (
  filename: string | null | undefined,
): string => {
  const lower = (filename || '').toLowerCase();
  if (lower.endsWith('.pdf')) return 'PDF';
  if (lower.endsWith('.docx')) return 'Word';
  if (lower.endsWith('.txt')) return 'TXT';
  return 'document';
};

export const contextAvailabilityLabel = (item: ContextItem): string => {
  const ingestion = getIngestionStatus(item);
  if (item.item_type === 'document') {
    if (ingestion === 'processing') return 'Processing';
    if (ingestion === 'failed') return 'Indexing failed';
    return 'Ready to use';
  }
  if (item.apply_mode === 'always') return 'In use';
  return 'Ready to use';
};
